import { useQuery } from "@tanstack/react-query";
import { meiliClient } from "../../../utils/meiliClient";
// --- NO CHANGE NEEDED HERE, JUST CONFIRMING THE IMPORT ---
import { FilterRow, toMeiliFilter } from "../../../types/meili";
import { CandidateInterface } from "./dto";
import { db } from "../../../firebase/firebase";
import { doc, getDoc } from "firebase/firestore";

export interface SearchParams {
  q: string;
  page: number;
  pageSize: number;
  filters: FilterRow[];
  indexName?: string;
  jobId?: string | null;
}

// Helper function to create a Firestore-like timestamp object
const createTimestamp = (seconds: number, nanoseconds: number) => {
  if (!seconds) return null;
  return {
    toDate: () => new Date(seconds * 1000 + nanoseconds / 1000000),
  };
};

export function useSearchCandidates(params: SearchParams | null) {
  return useQuery({
    queryKey: ["meiliSearchCandidates", params],
    enabled:
      !!params &&
      typeof params.page === "number" &&
      typeof params.pageSize === "number",
    queryFn: async () => {
      const index = meiliClient.index(params!.indexName || "applications");
      const offset = (params!.page - 1) * params!.pageSize;

      // --- THIS IS THE FINAL FIX ---
      // This logic now correctly handles the array of strings from your toMeiliFilter function.
      const filterConditions: string[] = [];

      // 1. Add the mandatory jobId filter
      if (params!.jobId) {
        filterConditions.push(`jobId = '${params!.jobId}'`);
      }

      // 2. Add all clauses from the user-generated filters
      if (params!.filters?.length) {
        const dynamicFilterClauses = toMeiliFilter(params!.filters);
        if (dynamicFilterClauses.length > 0) {
          // Use the spread operator (...) to add each clause from the array
          filterConditions.push(...dynamicFilterClauses);
        }
      }

      // 3. Join all conditions together with AND
      const filter = filterConditions.join(" AND ");
      // ----------------------------

      let searchRes: any;
      try {
        searchRes = await index.search(params!.q || "", {
          filter: filter || undefined,
          offset,
          limit: params!.pageSize,
          sort: ["appliedAt__seconds:desc"],
        });
      } catch (e1) {
        try {
          searchRes = await index.search(params!.q || "", {
            filter: filter || undefined,
            offset,
            limit: params!.pageSize,
          });
        } catch (e2) {
          searchRes = await index.search(params!.q || "", {
            offset,
            limit: params!.pageSize,
          });
        }
      }

      let job: any = null;
      if (params!.jobId) {
        const jobSnap = await getDoc(doc(db, "jobs", params!.jobId));
        job = jobSnap.exists() ? { ...jobSnap.data(), id: jobSnap.id } : null;
      }

      const hits = (searchRes.hits as any[]).map(
        (h): CandidateInterface & any => {
          const educationalBackground = (
            h.applicant_info_educationalBackground || []
          ).map((edu: any) => ({
            ...edu,
            startDate: createTimestamp(
              edu.startDate?._seconds,
              edu.startDate?._nanoseconds
            ),
            endDate: createTimestamp(
              edu.endDate?._seconds,
              edu.endDate?._nanoseconds
            ),
          }));

          const experience = (h.applicant_info_experience || []).map(
            (exp: any) => ({
              ...exp,
              startDate: createTimestamp(
                exp.startDate?._seconds,
                exp.startDate?._nanoseconds
              ),
              endDate: createTimestamp(
                exp.endDate?._seconds,
                exp.endDate?._nanoseconds
              ),
            })
          );

          return {
            id: h.id,
            jobId: h.jobId,
            job,
            status: h.status,
            questionAnswers: Object.fromEntries(
              Object.entries(h)
                .filter(([k]) => k.startsWith("questionAnswers_"))
                .map(([k, v]) => [k.replace("questionAnswers_", ""), v])
            ),
            coverLetter: h.coverLetter || "",
            appliedAt: createTimestamp(
              h.appliedAt__seconds,
              h.appliedAt__nanoseconds
            ),
            applicant_info: {
              uid: h.applicant_info_uid,
              email: h.applicant_info_email,
              firstName: h.applicant_info_firstName,
              lastName: h.applicant_info_lastName,
              phoneNumber: h.applicant_info_phoneNumber,
              gender: h.applicant_info_gender,
              languages: (h.applicant_info_languages || []) as string[],
              dateOfBirth: h.applicant_info_dateOfBirth,
              permanentAddress: {
                province: h.applicant_info_permanentAddress_province,
                district: h.applicant_info_permanentAddress_district,
                municipality: h.applicant_info_permanentAddress_municipality,
                ward: h.applicant_info_permanentAddress_ward,
              },
              temporaryAddress: {
                province: h.applicant_info_temporaryAddress_province,
                district: h.applicant_info_temporaryAddress_district,
                municipality: h.applicant_info_temporaryAddress_municipality,
                ward: h.applicant_info_temporaryAddress_ward,
              },
              age: h.applicant_info_age,
              certificates: h.applicant_info_certificates || [],
              cv: { file: h.applicant_info_cv_file },
              educationalBackground,
              experience,
            },
            resume: h.resume,
          };
        }
      );

      return {
        data: hits,
        nextCursor: null,
        totalCount:
          (searchRes as any).estimatedTotalHits ??
          (searchRes as any).totalHits ??
          0,
        page: params!.page,
        pageSize: params!.pageSize,
      };  
    },
    placeholderData: (prev) => prev,
  });
}